/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { Container, createStyles, List, ListItem, ListItemText, Paper, Theme, TablePagination } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { Set } from "immutable";
import React, { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { AppState, FollowUpsMap, nearestDeadline, syncSystem } from "../../redux";
import FollowUpFacilityListItem from "./FollowUpFacilityItem";
import { Alert } from '@material-ui/lab';
import { useRouteMatch } from 'react-router';
import LoadingIndicator from './form/components/LoadingIndicator';

const useStyles = makeStyles((theme: Theme) => {
  return createStyles({
    root: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      zIndex: 0,
      [theme.breakpoints.up("xs")]: {
        overflowY: "auto",
      },
      WebkitOverflowScrolling: "touch",
    },
    container: {
      marginBottom: theme.spacing(3),
      marginTop: theme.spacing(3),
    },
    alert: {
      marginBottom: theme.spacing(3)
    },
    list: {
      // marginBottom: theme.spacing(10),
      display: "flex",
      flexDirection: "column",
    },
    listEl: {
      paddingBottom: (props: FollowUpFacilitiesProperties) => props.preview ? 0 : '10px',
    },
    loading: {
      backgroundColor: "#fff",
    },
    pagination: {},
  });
});

export const selectFollowUps = (state: AppState) => state.followUps.items;
const selectFetchingFollowUpsState = (state: AppState) => state.followUps.fetchingAll || state.followUps.fetchingForFacility;
const selectFetchingFacilitiesState = (state: AppState) => state.facilities.fetching;

interface FollowUpFacilitiesProperties {
  limit?: number;
  preview?: boolean;
  classes?: Partial<Record<'root' | 'list' | 'loading', string>>
 }

const FollowUpFacilities: FC<FollowUpFacilitiesProperties> = (props = { preview: false }) => {
  const classes = useStyles(props);
  const followUpsByFacility = useSelector(selectFollowUps);
  // const error = useSelector(selectError);
  // const loadingFacilities = useSelector(selectLoadingFacilities);
  const fetchingFollowUps = useSelector(selectFetchingFollowUpsState);
  const fetchingFacilities = useSelector(selectFetchingFacilitiesState);

  const dispatch = useDispatch();

  const isFollowUpsPage = useRouteMatch('/follow-ups');

  useEffect(
    function refresh() {
      dispatch(syncSystem());
    },
    [dispatch]
  );

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [totalFacilities, setTotalFacilities] = useState(0);
  const [visibleFacilities, setVisibleFacilities] = useState<string[]>([]);

  useEffect(() => {
    setTotalFacilities(followUpsByFacility.size);
  }, [followUpsByFacility])

  useEffect(() => {
    const facilities = Set(followUpsByFacility.keys())
      .toArray()
      .sort(sortFacilities(followUpsByFacility))
      .slice(
        props.preview ? 0 : page * rowsPerPage,
        props.preview ? props.limit : page * rowsPerPage + rowsPerPage
      )

    setVisibleFacilities(facilities);
  }, [followUpsByFacility, rowsPerPage, page, props.limit])

  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const content =
    followUpsByFacility.count() > 0 ? (
      <>
        {
          visibleFacilities.map((facilityId) => (
            <FollowUpFacilityListItem key={facilityId} facilityId={facilityId} />
          ))
        }
      </>
    ) : (
        <ListItem  >
          <ListItemText primary="No Action Plans Due in the next 30 days" />
        </ListItem>
      );

  return (
    <div className={classes.root}>
      <Container maxWidth="md" className={classes.container}>
        { isFollowUpsPage && (
          <Alert severity="info" className={classes.alert}>
            Only facilities with one or more action plans due within 30 days (or overdue) are displayed in this list.  However, by selecting a facility you will be able to review and resolve any action plan that exists for the facility, regardless of due date.
          </Alert>
        )}
        <Paper elevation={1} className={classes.list}>
          <List className={classes.listEl}>
            { (fetchingFollowUps || fetchingFacilities) ? (
              <LoadingIndicator
                classes={{
                  container: classes.loading,
                }}
              >
                Loading Follow-Ups
              </LoadingIndicator>
            ) : content}
          </List>
          { !props.preview  && (
            <TablePagination
                count={totalFacilities}
                rowsPerPage={rowsPerPage}
                component="div"
                page={page}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
              />
          )}
        </Paper>
      </Container>
    </div>
  );
};

export default FollowUpFacilities;

function sortFacilities(followUps: FollowUpsMap) {
  
  return function (a: string, b: string): number {
    const aEarliest = followUps.getIn([a, 0]);
    const bEarliest = followUps.getIn([b, 0]);

    if (!aEarliest && !bEarliest) {
      return 0;
    }

    if (aEarliest && !bEarliest) {
      return 1;
    }

    if (!aEarliest && bEarliest) {
      return -1;
    }

    const aDate = aEarliest.data.due ?? nearestDeadline(aEarliest.data);
    const bDate = bEarliest.data.due ?? nearestDeadline(bEarliest.data);

    return aDate.localeCompare(bDate);
  }
}
